import {
    DestinationEvent,
    FormattedEvent,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import {
    deepKeysToCamel,
    transformUtil,
} from '@eventbrite/transformation-utils';
import { arrayOf, Schema } from 'normalizr';
import {
    OrganicEventSearchApiResponse,
    OrganicEventSearchFiltersApiPayload,
} from '../api/searchOrganicEvents';
import { ONLINE_BANNER_URL } from '../constants/constants';
import { Branding, EventSearch, PaginationData } from '../types';

export const getTransformedContent = (key: any, response: any) => {
    /* Due to how the normalizr library is handling this instantiation
   server side, necessary to do an additional interpolation step. */
    const content = new Schema(`${key}`);

    return transformUtil({
        response,
        schema: {
            [key]: arrayOf(content),
        },
    });
};

export type SearchResponseEvents = {
    events: SearchResponseEventsData;
    pagination: PaginationData;
};

export type SearchResponseEventsData = {
    organic: FormattedEvent[];
    promoted: FormattedEvent[];
    topMatches: FormattedEvent[];
};

export const parseSearchDataToEvents = (
    searchData: OrganicEventSearchApiResponse,
): SearchResponseEvents => {
    const { events } = searchData;

    return {
        events: {
            organic: parseApiEventData(events.results),
            promoted: parseApiEventData(events.promoted_results),
            topMatches: parseApiEventData(events.top_match),
        },
        pagination: {
            page: events.pagination.page_number,
            pageSize: events.pagination.page_size,
            pageCount: events.pagination.page_count,
            objectCount: events.pagination.object_count,
            continuation: events.pagination.continuation,
        },
    };
};

function parseApiEventData(
    events: DestinationEvent[] | undefined = [],
): FormattedEvent[] {
    return events.map(transformDestinationEvent);
}

export const parseSearchCriteria = (
    apiSearchCriteria: OrganicEventSearchFiltersApiPayload,
): EventSearch => {
    return deepKeysToCamel(apiSearchCriteria);
};

export const transformBrandingResponse = (
    branding: Branding,
    isOnline?: boolean,
) => {
    const {
        attribution: {
            name: artistName,
            summary: artistSummary,
            image: { original: { url: artistImageUrl } = {} as any } = {},
            social_links: artistSocialLinks,
        } = {} as any,
        header: {
            image: {
                original: { url: headerImageUrl } = {} as any,
                url: headerSmallImageUrl,
            } = {} as any,
        } = {},
        middle: {
            image: {
                original: { url: middleImageUrl } = {} as any,
                url: middleSmallImageUrl,
            } = {} as any,
        } = {},
        footer: {
            image: {
                original: { url: footerImageUrl } = {} as any,
                url: footerSmallImageUrl,
            } = {} as any,
        } = {},
        decoration_top_left: {
            image: { original: { url: decorationTopLeftUrl } = {} as any } = {},
        } = {},
        decoration_top_right: {
            image: {
                original: { url: decorationTopRightUrl } = {} as any,
            } = {},
        } = {},
        decoration_bottom_left: {
            image: {
                original: { url: decorationBottomLeftUrl } = {} as any,
            } = {},
        } = {},
        decoration_bottom_right: {
            image: {
                original: { url: decorationBottomRightUrl } = {} as any,
            } = {},
        } = {},
    } = branding;

    let onlineHeaderImage = null;

    if (isOnline) {
        onlineHeaderImage = ONLINE_BANNER_URL;
    }

    return {
        artistName,
        artistSummary,
        artistImageUrl,
        headerImageUrl: onlineHeaderImage || headerImageUrl,
        headerSmallImageUrl: onlineHeaderImage || headerSmallImageUrl,
        middleImageUrl,
        middleSmallImageUrl,
        footerImageUrl,
        footerSmallImageUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        artistSocialLinks,
    };
};
